import { useQueryClient } from "@tanstack/react-query";
import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { useToast } from "@repo/ui";
import { actionQueryKey } from "~/pages/administration/workflows-page/hooks/apis/use-action-mutations";
import { useWorkflowCreationContext } from "~/pages/administration/workflows-page/workflow-creation-context-provider";
import { hasActionsChanged } from "~/pages/administration/workflows-page/workflows-dialog/views/action-view/utils/detect-action-change";
import { validateAction } from "~/pages/administration/workflows-page/workflows-dialog/views/action-view/utils/validate-actions";
import type {
  ActionDTO,
  ActionUpdateBody,
  CreateActionRequest,
  PrintPdfConfiguration,
} from "~/types/api";

export type CreateLocalActionRequest = CreateActionRequest & {
  id?: number | null;
  statusCodes?: number[];
  sortOrder?: number;
};

export type ActionRow = {
  id: string;
  name: string;
  originalObj: CreateLocalActionRequest;
};

const serverActionsToLocal = (server: ActionDTO[]): CreateLocalActionRequest[] =>
  [...server]
    .map((p) => ({
      id: p.id,
      name: p.name,
      configuration: p.configuration,
      statusCodes: (p.statuses ?? []).map((s) => s.statusCode),
      sortOrder: p.statuses?.[0]?.sortOrder ?? 0,
    }))
    .sort((a, b) => a.sortOrder - b.sortOrder);

type Props = {
  editModeActions?: ActionDTO[];
  isEditMode?: boolean;
  workflowId?: number;
  updateAction?: (workflowId: number, actionId: number, body: ActionUpdateBody) => Promise<unknown>;
  deleteAction?: (workflowId: number, actionId: number) => Promise<unknown>;
  createActions?: (
    workflowId: number,
    actionsMap: CreateLocalActionRequest[]
  ) => Promise<PromiseSettledResult<unknown>[]>;
  linkStatus?: (
    workflowId: number,
    actionId: number,
    statusCode: number,
    sortOrder: number
  ) => Promise<unknown>;
  refetch?: () => Promise<unknown>;
  onClose?: () => void;
};

export const useActionView = ({
  editModeActions,
  isEditMode,
  workflowId,
  updateAction,
  deleteAction,
  createActions,
  linkStatus,
  refetch,
  onClose,
}: Props) => {
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const {
    state: { actions: allActions },
    updateState,
  } = useWorkflowCreationContext();

  const snapshotRef = useRef<CreateLocalActionRequest[]>(allActions);
  const hasSeededRef = useRef(false);
  const hasReorderedRef = useRef(false);

  const revertToSnapshot = useCallback(() => {
    updateState("actions", snapshotRef.current);
  }, [updateState]);

  useEffect(() => {
    if (
      !hasSeededRef.current &&
      editModeActions &&
      editModeActions.length > 0 &&
      allActions.length === 0
    ) {
      const seeded = serverActionsToLocal(editModeActions);
      hasSeededRef.current = true;
      snapshotRef.current = seeded;
      updateState("actions", seeded);
    }
  }, [editModeActions]);

  const reLinkActionsWithChangedSortOrder = useCallback(async () => {
    if (!linkStatus || !workflowId) return;
    const relinkPromises: Promise<unknown>[] = [];
    allActions.forEach((action, index) => {
      if (action.id == null) return;
      const serverAction = editModeActions?.find((e) => e.id === action.id);
      if (!serverAction) return;
      (action.statusCodes ?? []).forEach((code) => {
        const serverSortOrder =
          serverAction.statuses?.find((s) => s.statusCode === code)?.sortOrder ?? 0;
        if (serverSortOrder !== index) {
          relinkPromises.push(linkStatus(workflowId, action.id as number, code, index));
        }
      });
    });

    if (relinkPromises.length > 0) {
      const reorderResults = await Promise.allSettled(relinkPromises);
      await queryClient.invalidateQueries({
        queryKey: actionQueryKey(workflowId),
      });
      const reorderOk = reorderResults.every((r) => r.status === "fulfilled");
      if (reorderOk) {
        showToast("success", "Action order updated successfully");
      } else {
        showToast("error", "Some actions failed to reorder", "Error");
      }
    }
  }, [allActions, editModeActions, workflowId, linkStatus, queryClient, showToast]);

  const handleBack = useCallback(() => {
    if (isEditMode) {
      updateState("actions", []);
    } else {
      revertToSnapshot();
      updateState("currentView", "default");
    }
    updateState("activeActionId", null);
    hasReorderedRef.current = false;

    onClose?.();
  }, [updateState, revertToSnapshot, isEditMode, onClose]);

  const handleSave = useCallback(async () => {
    if (isEditMode && workflowId) {
      const currentActions = allActions;

      const updatePromises = currentActions
        .map((action) => {
          const existingAction =
            action.id != null ? (editModeActions?.find((a) => a.id === action.id) ?? null) : null;

          if (!existingAction || !updateAction || !workflowId) return null;

          const hasChanges =
            existingAction.name !== action.name ||
            JSON.stringify(existingAction.configuration) !== JSON.stringify(action.configuration);

          if (!hasChanges) return null;

          return updateAction(workflowId, existingAction.id, {
            name: action.name,
            configuration: action.configuration,
          });
        })
        .filter(Boolean) as Promise<unknown>[];

      const toCreate = currentActions
        .filter((action) => !editModeActions?.some((a) => a.id === action.id))
        .map((action) => ({
          ...action,
          sortOrder: currentActions.indexOf(action),
        }));

      const toDelete = (editModeActions ?? []).filter(
        (editAction) => !currentActions.some((a) => a.id === editAction.id)
      );

      const updateResults = await Promise.allSettled(updatePromises);

      let createResults: PromiseSettledResult<unknown>[] = [];
      if (toCreate.length > 0 && createActions && workflowId) {
        createResults = await createActions(workflowId, toCreate);
      }

      let deleteResults: PromiseSettledResult<unknown>[] = [];
      if (toDelete.length > 0 && deleteAction && workflowId) {
        deleteResults = await Promise.allSettled(
          toDelete.map((action) => deleteAction(workflowId, action.id))
        );
      }

      if (hasReorderedRef.current) {
        await reLinkActionsWithChangedSortOrder();
      }

      await refetch?.();

      const updatesOk =
        updatePromises.length === 0 ? true : updateResults.every((r) => r.status === "fulfilled");
      const createsOk =
        toCreate.length === 0
          ? true
          : createResults.length === toCreate.length &&
            createResults.every((r) => r.status === "fulfilled");
      const deletesOk =
        toDelete.length === 0
          ? true
          : deleteResults.length === toDelete.length &&
            deleteResults.every((r) => r.status === "fulfilled");

      if (updatesOk && createsOk && deletesOk) {
        handleBack();
      }
    } else {
      snapshotRef.current = allActions;
      updateState("activeActionId", null);
      handleBack();
    }
  }, [
    allActions,
    editModeActions,
    handleBack,
    isEditMode,
    workflowId,
    updateAction,
    deleteAction,
    createActions,
    reLinkActionsWithChangedSortOrder,
    updateState,
    refetch,
  ]);

  const localActions = allActions;

  const hasChanges = useMemo(() => {
    if (isEditMode) {
      const original = serverActionsToLocal(editModeActions ?? []);
      return hasActionsChanged(original, allActions);
    }
    return hasActionsChanged(snapshotRef.current, allActions);
  }, [allActions, editModeActions, isEditMode]);

  const hasValidationErrors = useMemo(() => {
    return allActions.some((action) => {
      const cfg = action.configuration as PrintPdfConfiguration | undefined;
      const isDynamic = cfg?.presetName === null || cfg?.presetNameFieldId != null;
      const errors = validateAction(action, isDynamic);
      return Object.values(errors).some(Boolean);
    });
  }, [allActions]);

  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  const data = useMemo<ActionRow[]>(
    () =>
      localActions.map((action, index) => ({
        id: `${action.id ?? action.name}-${index}`,
        name: action.name,
        originalObj: action,
      })),
    [localActions]
  );

  const selectedAction = useMemo(() => {
    if (selectedIndex === null || !data[selectedIndex]) return null;
    return data[selectedIndex].originalObj;
  }, [selectedIndex, data]);

  const handleOnClickAdd = useCallback(() => {
    const newAction: CreateLocalActionRequest = {
      id: Date.now(),
      name: `Action ${localActions.length + 1}`,
      statusCodes: [],
      configuration: {
        Type: 1,
        presetName: "",
        presetNameFieldId: null,
        outputFieldId: 0,
        fileNameTemplate: "",
        method: "Spread",
        promptUser: false,
      },
    };
    const newActions = [...localActions, newAction];
    updateState("actions", newActions);
    setSelectedIndex(newActions.length - 1);
  }, [localActions, updateState]);

  const handleInputSave = useCallback(
    (updated: CreateLocalActionRequest) => {
      if (selectedIndex === null) return;
      const newActions = localActions.map((a, i) =>
        i === selectedIndex ? { ...a, ...updated } : a
      );
      updateState("actions", newActions);
    },
    [selectedIndex, localActions, updateState]
  );

  const handleDelete = useCallback(() => {
    if (selectedIndex === null) return;
    const newActions = localActions.filter((_, i) => i !== selectedIndex);
    updateState("actions", newActions);
    setSelectedIndex(null);
  }, [selectedIndex, localActions, updateState]);

  const handleReorder = useCallback(
    (newData: ActionRow[], oldIndex?: number, newIndex?: number) => {
      const reordered = newData.map((row) => row.originalObj);
      hasReorderedRef.current = true;
      updateState("actions", reordered);
      if (selectedIndex !== null && oldIndex !== undefined && newIndex !== undefined) {
        if (selectedIndex === oldIndex) setSelectedIndex(newIndex);
        else if (selectedIndex > oldIndex && selectedIndex <= newIndex)
          setSelectedIndex(selectedIndex - 1);
        else if (selectedIndex < oldIndex && selectedIndex >= newIndex)
          setSelectedIndex(selectedIndex + 1);
      }
    },
    [updateState, selectedIndex]
  );

  return {
    data,
    selectedAction,
    selectedIndex,
    setSelectedIndex,
    handleOnClickAdd,
    handleInputSave,
    handleDelete,
    handleReorder,
    handleSave,
    handleBack,
    revertToSnapshot,
    hasChanges,
    hasValidationErrors,
  };
};
